"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
// Capture the currently executing script immediately
// Must be at the top before any other code executes, as document.currentScript
// becomes null in async contexts
const currentScript = document.currentScript;
const react_1 = __importDefault(require("react"));
const client_1 = require("react-dom/client");
const constants_1 = require("../constants");
const CitrusBanner_1 = __importDefault(require("../components/CitrusBanner/CitrusBanner"));
const KevelBanner_1 = __importDefault(require("../components/KevelBanner/KevelBanner"));
const CitrusAnalytics_1 = __importDefault(require("../components/CitrusAnalytics/CitrusAnalytics"));
const KevelAnalytics_1 = __importDefault(require("../components/KevelAnalytics/KevelAnalytics"));
const KevelBannerPreviewer_1 = __importDefault(require("../components/KevelBannerPreviewer/KevelBannerPreviewer"));
const BrandAZSlots_1 = __importDefault(require("../components/BrandAZSlots/BrandAZSlots"));
const ContentBlocks_1 = __importDefault(require("../components/ContentBlocks/ContentBlocks"));
const isBrandAZSlotsData = (data) => {
    return (Array.isArray(data) &&
        data.length === 6 &&
        data.every((item) => typeof item === "object" &&
            item !== null &&
            "mobileImage" in item &&
            "tabletImage" in item));
};
const isContentBlocksData = (data) => {
    return (Array.isArray(data) &&
        (data.length === 2 || data.length === 4) &&
        data.every((item) => typeof item === "object" &&
            item !== null &&
            "logoPosition" in item &&
            "slotNumber" in item &&
            "designType" in item));
};
// Find banner data script relative to the root element (for ESI isolation)
const findBannerDataScript = (rootElem) => {
    // Look for script with data-esi-data attribute before the root element
    let sibling = rootElem.previousElementSibling;
    while (sibling) {
        if (sibling.tagName === "SCRIPT" && sibling.hasAttribute("data-esi-data")) {
            return sibling;
        }
        sibling = sibling.previousElementSibling;
    }
    // Fallback: When loaded in isolation, browser moves scripts to <head>
    // This maintains compatibility with both embedded (production) and isolated (testing) scenarios
    const scriptInHead = document.querySelector("head script[data-esi-data]");
    if (scriptInHead) {
        return scriptInHead;
    }
    return null;
};
// Find the banner root element that comes after a given script tag
// This allows each script execution to find its own banner in its ESI fragment
const findEsiRootFromScript = (scriptElem) => {
    if (!scriptElem)
        return null;
    // Traverse forward from the script tag to find the banner root div
    let sibling = scriptElem.nextElementSibling;
    while (sibling) {
        if (sibling.id === constants_1.BANNER_ROOT_ELEM_ID) {
            return sibling;
        }
        sibling = sibling.nextElementSibling;
    }
    return null;
};
// Hydrate a single ESI banner (reusable function for initial load and dynamic ESIs)
const hydrateESIBanner = (esiToHydrate) => {
    // Prevent duplicate hydration
    if (esiToHydrate.hasAttribute("data-hydrated")) {
        return;
    }
    const bannerDataElem = findBannerDataScript(esiToHydrate);
    if (!bannerDataElem) {
        return;
    }
    // Mark as hydrated immediately to prevent duplicate hydration
    esiToHydrate.setAttribute("data-hydrated", "true");
    const data = JSON.parse(bannerDataElem.textContent || "{}");
    if (isBrandAZSlotsData(data)) {
        (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(BrandAZSlots_1.default, { data: data }));
    }
    else if (isContentBlocksData(data)) {
        (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(ContentBlocks_1.default, { data: data }));
    }
    else if (data.associatedAdvertId) {
        const isKevelBanner = data.clickUrl && data.impressionUrl;
        if (isKevelBanner) {
            (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(KevelBanner_1.default, Object.assign({}, data)));
        }
        else {
            (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(CitrusBanner_1.default, Object.assign({}, data)));
        }
    }
    else {
        (0, client_1.hydrateRoot)(esiToHydrate, react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(CitrusAnalytics_1.default, null),
            react_1.default.createElement(KevelAnalytics_1.default, null)));
    }
};
// Hydrate THIS script's banner only (ESI isolation)
// Each ESI fragment includes its own main.js, so this code runs once per fragment
let esiToHydrate;
if (currentScript) {
    // Primary approach: Find the banner that belongs to the currently executing script
    esiToHydrate = findEsiRootFromScript(currentScript);
    // Fallback: If script-relative approach fails (script positioned after banner)
    if (!esiToHydrate) {
        const allBanners = document.querySelectorAll(`#${constants_1.BANNER_ROOT_ELEM_ID}`);
        esiToHydrate =
            Array.from(allBanners).find((elem) => !elem.hasAttribute("data-hydrated")) || null;
    }
}
else {
    // Fallback: If currentScript is null (bundlers, module scripts), find first unhydrated banner
    const allBanners = document.querySelectorAll(`#${constants_1.BANNER_ROOT_ELEM_ID}`);
    esiToHydrate =
        Array.from(allBanners).find((elem) => !elem.hasAttribute("data-hydrated")) || null;
}
if (esiToHydrate) {
    hydrateESIBanner(esiToHydrate);
}
// Watch for dynamically added ESI fragments (e.g., NavFlyout loaded on-demand)
// This ensures banners loaded after initial page load are still hydrated
const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node;
                // Check if the added node itself is a banner root
                if (element.id === constants_1.BANNER_ROOT_ELEM_ID &&
                    !element.hasAttribute("data-hydrated")) {
                    hydrateESIBanner(element);
                }
                // Check for banner roots within the added node
                if (element.querySelectorAll) {
                    const banners = element.querySelectorAll(`#${constants_1.BANNER_ROOT_ELEM_ID}:not([data-hydrated])`);
                    banners.forEach((banner) => {
                        hydrateESIBanner(banner);
                    });
                }
            }
        });
    });
});
// Start observing the document for dynamically added ESI fragments
observer.observe(document.body, {
    childList: true,
    subtree: true,
});
// Handle previewer separately (single instance)
const previewerDataElem = document.getElementById(constants_1.BANNER_PREVIEWER_DATA_ELEM_ID);
if (previewerDataElem) {
    const previewerRootElem = document.getElementById(constants_1.BANNER_PREVIEWER_ROOT_ELEM_ID);
    const data = JSON.parse(previewerDataElem.textContent || "{}");
    if (previewerRootElem) {
        (0, client_1.hydrateRoot)(previewerRootElem, react_1.default.createElement(KevelBannerPreviewer_1.default, Object.assign({}, data)));
    }
}
